// ResearchBrief + IndustryDossier (TASK-612) — the generative-UI face of the
// research desk. Renders TASK-610's dossier contract: cited thesis/sections
// (citation chips → source excerpt panel), an amber "Model view (uncited)"
// block that is never interleaved with cited text, and the COMPUTED book strip
// (client names, CHF, KG conflict/match badges). Self-fetches the latest
// dossier for its subject; a miss triggers generation with a staged status.
// Honest states throughout (601/602 contracts) — an empty corpus says so.
import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  researchApiService,
  type DossierSource,
  type ResearchDossier as Dossier,
  type SourceChunk,
} from '../../../services/api/researchApiService';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetErrorState } from '../../ui/WidgetErrorState';

const CHF = new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 });

/** Split markdown text on [n]/[n, m] markers and render them as clickable chips. */
function CitedText({
  text,
  sources,
  onCite,
}: {
  text: string;
  sources: DossierSource[];
  onCite: (n: number) => void;
}) {
  const parts = text.split(/(\[\d+(?:\s*,\s*\d+)*\])/g);
  return (
    <span className="whitespace-pre-line">
      {parts.map((part, i) => {
        const m = part.match(/^\[(\d+(?:\s*,\s*\d+)*)\]$/);
        if (!m) return <Fragment key={i}>{part}</Fragment>;
        return m[1].split(',').map((raw, j) => {
          const n = parseInt(raw.trim(), 10);
          const known = sources.some((s) => s.n === n);
          return (
            <button
              key={`${i}-${j}`}
              type="button"
              disabled={!known}
              onClick={() => onCite(n)}
              data-testid="citation-chip"
              className="mx-0.5 inline-flex items-center rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary hover:bg-primary/20 disabled:opacity-50"
            >
              {n}
            </button>
          );
        });
      })}
    </span>
  );
}

function SourcePanel({ source, onClose }: { source: DossierSource; onClose: () => void }) {
  const { data: chunks, isLoading } = useQuery<SourceChunk[]>({
    queryKey: ['research-source-chunks', source.doc_id, source.page],
    queryFn: () => researchApiService.getSourceChunks(source.doc_id, source.page),
    enabled: !source.doc_id.startsWith('news-'),
  });
  return (
    <div
      data-testid="source-panel"
      className="mt-2 rounded-md border border-border bg-accent/40 p-3 text-[12px]"
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="font-medium text-foreground">
          [{source.n}] {source.title}
          {source.page ? ` · p.${source.page}` : ''}
        </span>
        <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
          ✕
        </button>
      </div>
      <div className="mb-2 text-muted-foreground">
        {[source.issuer, source.origin, source.published_at?.slice(0, 10)]
          .filter(Boolean)
          .join(' · ')}
      </div>
      {source.doc_id.startsWith('news-') ? (
        <p className="text-muted-foreground">News headline — no stored page excerpt.</p>
      ) : isLoading ? (
        <div className="h-10 animate-pulse rounded bg-accent" />
      ) : chunks?.length ? (
        <p className="whitespace-pre-line text-muted-foreground">
          {chunks.map((c) => c.text).join('\n\n')}
        </p>
      ) : (
        <p className="text-muted-foreground">Excerpt unavailable for this source.</p>
      )}
    </div>
  );
}

function BookStrip({ dossier }: { dossier: Dossier }) {
  const navigate = useNavigate();
  const [drafting, setDrafting] = useState<string | null>(null);
  const book = dossier.book_impact;
  if (!book) return null;

  const draftNote = async (clientId: string) => {
    setDrafting(clientId);
    try {
      // TASK-613: research → workbench docx (the FinSA suitability artifact).
      const res = await researchApiService.draftRationaleNote(dossier.dossier_id, clientId);
      navigate(`/workbench/${res.document_id}`);
    } catch {
      setDrafting(null);
    }
  };
  return (
    <div data-testid="book-strip" className="mt-3 rounded-md border border-border p-3">
      <div className="mb-2 text-[12px] font-semibold text-foreground">
        Your book
        {book.clients.length > 0 ? (
          <span className="ml-2 font-normal text-muted-foreground">
            {book.clients.length} client{book.clients.length > 1 ? 's' : ''}
            {book.total_exposure_chf != null &&
              ` · CHF ${CHF.format(book.total_exposure_chf)}${book.has_unknown_exposure ? '+ (partly unknown)' : ''} total`}
          </span>
        ) : (
          <span className="ml-2 font-normal text-muted-foreground">
            no holdings or client-value matches
          </span>
        )}
      </div>
      {book.clients.map((c) => (
        <div key={c.client_id} className="flex flex-wrap items-center gap-1.5 py-0.5 text-[12px]">
          <span className="font-medium text-foreground">{c.name}</span>
          <span className="text-muted-foreground">
            {c.exposure_chf != null ? `CHF ${CHF.format(c.exposure_chf)}` : 'exposure unknown'}
          </span>
          {c.kg_conflicts.map((k) => (
            <span key={k} className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-800 dark:bg-red-900/40 dark:text-red-300" title={k}>
              ⚠ {k.length > 40 ? `${k.slice(0, 40)}…` : k}
            </span>
          ))}
          {c.kg_matches.map((k) => (
            <span key={k} className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" title={k}>
              ✚ {k.length > 40 ? `${k.slice(0, 40)}…` : k}
            </span>
          ))}
          <button
            type="button"
            disabled={drafting === c.client_id}
            onClick={() => void draftNote(c.client_id)}
            data-testid="draft-rationale-note"
            className="ml-auto text-[11px] text-primary hover:underline disabled:opacity-50"
          >
            {drafting === c.client_id ? 'Drafting…' : 'Draft rationale note'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/clients/${c.client_id}`)}
            className="text-[11px] text-primary hover:underline"
          >
            Open positions
          </button>
        </div>
      ))}
    </div>
  );
}

function DossierShell({
  subjectType,
  subject,
  title,
}: {
  subjectType: 'company' | 'industry';
  subject: string;
  title: string;
}) {
  const queryClient = useQueryClient();
  const [openSource, setOpenSource] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [noSources, setNoSources] = useState(false);

  const queryKey = ['research-dossier', subjectType, subject];
  const { data, isLoading, isError, refetch } = useQuery<Dossier | null>({
    queryKey,
    queryFn: async () => {
      const latest = await researchApiService.getLatestDossier(subjectType, subject);
      if (latest) return latest;
      // Miss → generate (one LLM call). Kept inside the queryFn so the widget
      // owns its whole lifecycle; status is surfaced via the generating flag.
      setGenerating(true);
      try {
        const generated = await researchApiService.generateDossier(subjectType, subject);
        if ('success' in generated && generated.success === false) {
          setNoSources(generated.error === 'no_sources');
          return null;
        }
        return generated as Dossier;
      } finally {
        setGenerating(false);
      }
    },
    enabled: !!subject,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const dossier = data ?? null;
  const source = dossier?.sources.find((s) => s.n === openSource) ?? null;

  return (
    <WidgetContainer
      title={`${title} · ${subject}`}
      source={`Research · ${dossier ? `${dossier.sources.length} sources` : 'corpus'}`}
    >
      {!subject && (
        <div className="text-[13px] text-muted-foreground">
          No subject given — ask e.g. “analyze Novartis for my book”.
        </div>
      )}
      {subject && isLoading && (
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground/40 border-t-transparent" />
          {generating ? 'Reading sources and writing the dossier…' : 'Looking for a recent dossier…'}
        </div>
      )}
      {subject && isError && (
        <WidgetErrorState
          title="Could not load the research dossier"
          onRetry={() => {
            void queryClient.invalidateQueries({ queryKey });
            void refetch();
          }}
        />
      )}
      {subject && !isLoading && !isError && !dossier && (
        <div className="text-[13px] text-muted-foreground" data-testid="dossier-empty">
          {noSources
            ? `No research sources on file for ${subject} — upload a broker note or add it to a watchlist, then try again.`
            : `No dossier could be produced for ${subject}.`}
        </div>
      )}
      {dossier && (
        <div className="text-[13px]">
          {dossier.caveat && (
            <div className="mb-2 text-[11px] text-muted-foreground">{dossier.caveat}</div>
          )}
          {dossier.thesis && (
            <p className="mb-2 font-medium text-foreground" data-testid="dossier-thesis">
              <CitedText text={dossier.thesis} sources={dossier.sources} onCite={setOpenSource} />
            </p>
          )}
          {dossier.sections.map((s) => (
            <details key={s.title} open className="mb-2">
              <summary className="cursor-pointer text-[12px] font-semibold text-foreground">
                {s.title}
              </summary>
              <p className="mt-1 text-muted-foreground">
                <CitedText text={s.md} sources={dossier.sources} onCite={setOpenSource} />
              </p>
            </details>
          ))}
          {dossier.model_view && (
            <div
              data-testid="model-view"
              className="mb-2 rounded-md border border-amber-300/60 bg-amber-50 p-2 text-[12px] text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200"
            >
              <div className="mb-1 font-semibold">⚠ Model view (uncited)</div>
              <p className="whitespace-pre-line">{dossier.model_view}</p>
            </div>
          )}
          <BookStrip dossier={dossier} />
          <div className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
            Sources:{' '}
            {dossier.sources.map((s) => (
              <button
                key={s.n}
                type="button"
                onClick={() => setOpenSource(s.n)}
                className="mr-2 hover:text-foreground hover:underline"
              >
                [{s.n}] {s.title.length > 40 ? `${s.title.slice(0, 40)}…` : s.title}
                {s.page ? ` · p.${s.page}` : ''}
              </button>
            ))}
          </div>
          {source && <SourcePanel source={source} onClose={() => setOpenSource(null)} />}
        </div>
      )}
    </WidgetContainer>
  );
}

export function ResearchBrief({ subject = '' }: { subject?: string }) {
  return <DossierShell subjectType="company" subject={subject} title="Research Brief" />;
}

export function IndustryDossier({ subject = '' }: { subject?: string }) {
  return <DossierShell subjectType="industry" subject={subject} title="Industry Dossier" />;
}
